<template>
  <section>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <b-select
              v-model="selectedArea"
              :loading="loadingAreas"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option :value="null">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in areas"
                :value="option"
                :key="option.code"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
        </div>

        <div class="level-item">
          <b-field>
            <b-select
              v-model="selectedLecture"
              :loading="loadingLectures"
              :placeholder="
                $t('message.please_select') + $t('attributes.lecture')
              "
              @input="onSelectLecture"
            >
              <option :value="null">
                {{ $t('attributes.all') }}
              </option>
              <option
                v-for="option in lectures"
                :value="option"
                :key="option.id"
              >
                {{ option.topic }}
              </option>
            </b-select>
          </b-field>
        </div>

        <div class="level-item">
          <b-field>
            <b-datepicker
              :placeholder="
                $t('message.please_select') + $t('attributes.date_range')
              "
              v-model="selectedDates"
              range
              @input="onSelectedDates"
            >
              <button class="button is-danger" @click="onClearDates">
                <b-icon icon="close"></b-icon>
                <span>Clear</span>
              </button>
            </b-datepicker>
          </b-field>
        </div>
        <div class="level-item">
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="
                $t('attributes.search') + $t('attributes.contact_person')
              "
              type="search"
              icon="magnify"
              @input="onFilterStatus"
            ></b-input>
          </b-field>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <b-field>
            <b-button :loading="exporting" type="is-info" @click="onExport">
              {{ $t('message.export_reports') }}
            </b-button>
          </b-field>
        </div>
      </div>
    </nav>

    <b-table
      ref="table"
      class="lecture-table"
      :data="items"
      :loading="isLoading"
      :mobile-cards="true"
      hoverable
      narrowed
      scrollable
      detailed
      detail-key="order_no"
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="sequence"
        :label="$t('attributes.lecture_sequence')"
        width="80"
        sortable
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.sequence }}
      </b-table-column>

      <b-table-column
        field="order_no"
        :label="$t('attributes.signup_no')"
        width="110"
        sortable
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.order_no }}
      </b-table-column>

      <b-table-column
        field="create_date"
        :label="$t('attributes.signup_date')"
        width="170"
        sortable
        v-slot="props"
      >
        {{ formatDateTime(props.row.create_date) }}
      </b-table-column>

      <b-table-column
        field="area_name"
        :label="$t('attributes.area_name')"
        width="120"
        sortable
        v-slot="props"
      >
        <p v-html="props.row.area_code"></p>
      </b-table-column>

      <b-table-column
        field="product_name"
        :label="$t('attributes.lecture')"
        sortable
        cell-class="product-name"
        v-slot="props"
      >
        <p v-html="props.row.product_name"></p>
      </b-table-column>

      <b-table-column
        field="customer_name"
        :label="$t('attributes.contact_person')"
        width="100"
        sortable
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.customer_name }}
      </b-table-column>

      <b-table-column
        field="passengers_count"
        :label="$t('attributes.signup_quantity')"
        width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.passengers_count }}
      </b-table-column>

      <b-table-column
        field="customer_address"
        :label="$t('attributes.line_id')"
        width="120"
        sortable
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.customer_address }}
      </b-table-column>

      <b-table-column
        field="customer_tel"
        :label="$t('attributes.tel')"
        width="120"
        sortable
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.customer_tel }}
      </b-table-column>

      <b-table-column
        field="customer_email"
        :label="$t('attributes.email')"
        width="120"
        sortable
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.customer_email }}
      </b-table-column>

      <b-table-column
        field="know_fantasy"
        :label="$t('attributes.lecture_know_fantasy')"
        width="120"
        v-slot="props"
      >
        {{ props.row.know_fantasy }}
      </b-table-column>

      <b-table-column
        field="customer_remark"
        :label="$t('attributes.lecture_notes')"
        width="120"
        v-slot="props"
      >
        {{ props.row.customer_remark }}
      </b-table-column>

      <template #detail="props">
        <article class="media" style="margin: 0 1.5rem;">
          <div class="media-content">
            <div class="content">
              <p>
                <strong>{{ $t('attributes.questionnaire') }}</strong>
              </p>
            </div>
            <div v-if="props.row.questionnaire">
              <div
                v-for="question in props.row.questionnaire.questions"
                :key="question.id"
              >
                <p>{{ question.title }}: {{ question.result }}</p>
              </div>
            </div>
          </div>
        </article>
      </template>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>

<script>
import { AREA_API, LECTURE_ORDERS_API, LECTURES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'LectureList',

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    const { startDate, endDate } = this.calculateDateRange(this.query)

    return {
      loading: false,
      loadingItems: false,
      exporting: false,
      total: 0,
      items: [],

      searchName: this.query.search || '',
      sortField: this.query.sort || 'create_date',
      sortOrder: this.query.order || 'desc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      selectedDates: [startDate, endDate],
      startDate,
      endDate,

      loadingAreas: false,
      areas: [],
      selectedArea: null,
      selectedAreaCode: this.query.ac || null,

      loadingLectures: false,
      lectures: [],
      selectedLecture: null,
      selectedLectureId: this.query.l || null
    }
  },
  watch: {
    query(newQuery) {
      this.getQuery(newQuery)
      this.load()
    }
  },
  computed: {
    isLoading() {
      return (
        this.loading ||
        this.loadingItems ||
        this.loadingAreas ||
        this.loadingLectures
      )
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      await this.loadAreas()
      await this.loadLectures()
      await this.loadItems()
      this.loading = false
    },
    async load() {
      this.loading = true
      await this.loadLectures()
      await this.loadItems()
      this.loading = false
    },
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
          this.selectedAreaCode = this.selectedArea && this.selectedArea.code
        }

        if (this.selectedAreaCode) {
          await this.loadLectures()
        } else {
          this.lectures = []
          this.selectedLecture = null
          this.selectedLectureId = null
        }
      } catch (e) {
        this.areas = []
        this.selectedArea = null
        this.selectedAreaCode = null

        this.lectures = []
        this.selectedLecture = null
        this.selectedLectureId = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadLectures() {
      try {
        const params = {
          area_code: this.selectedAreaCode
        }

        this.loadingLectures = true
        const res = await this.axios.get(`${LECTURES_API}/list`, { params })
        const lectures = res.data.data
        this.lectures = lectures.sort(a => a.sort)
        this.selectedLecture = null

        if (this.selectedLectureId) {
          this.selectedLecture = lectures.find(
            a => a.id === this.selectedLectureId
          )
          this.selectedLectureId =
            this.selectedLecture && this.selectedLecture.id
        }
      } catch (e) {
        this.lectures = []
        this.selectedLecture = null
        this.selectedLectureId = null

        notifyService.fetch_error('lecture', e)
      } finally {
        this.loadingLectures = false
      }
    },
    async loadItems() {
      try {
        const params = {
          area_code: this.selectedAreaCode,
          code: this.selectedLectureId,
          search: this.searchName,
          start_date: this.startDate,
          end_date: this.endDate,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(LECTURE_ORDERS_API, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('lecture_order', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.searchName = query.search || ''
      this.sortField = query.sort || 'create_date'
      this.sortOrder = query.order || 'desc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10

      this.selectedArea = null
      this.selectedAreaCode = query.ac || null

      this.selectedLecture = null
      this.selectedLectureId = query.l || null

      if (this.selectedAreaCode) {
        this.selectedArea = this.areas.find(
          a => a.code === this.selectedAreaCode
        )
      }

      if (this.selectedLectureId) {
        this.selectedLecture = this.lectures.find(
          a => a.id === this.selectedLectureId
        )
      }

      const { startDate, endDate } = this.calculateDateRange(query)
      this.startDate = startDate
      this.endDate = endDate
      this.selectedDates = [startDate, endDate]
    },
    calculateDateRange(query) {
      let startDate = null
      let endDate = null

      if (query.start && query.end) {
        startDate = this.$dayjs.unix(query.start).toDate()
        endDate = this.$dayjs.unix(query.end).toDate()
      } else {
        const today = this.$dayjs()
        startDate = today.subtract(1, 'year').toDate()
        // startDate = today.startOf('month').toDate()
        endDate = today.toDate()
      }

      return { startDate, endDate }
    },
    updateQuery() {
      const query = {
        ac: this.selectedAreaCode,
        l: this.selectedLectureId,
        search: this.searchName,
        start: this.startDate && this.$dayjs(this.startDate).unix(),
        end: this.endDate && this.$dayjs(this.endDate).unix(),
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.page = 1
      this.updateQuery()
    },
    onSelectArea(option) {
      this.page = 1
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null
      this.lectures = []
      this.selectedLecture = null
      this.selectedLectureId = null

      this.updateQuery()
    },
    onSelectLecture(option) {
      this.page = 1
      this.selectedLecture = option
      this.selectedLectureId = option ? option.id : null

      this.updateQuery()
    },
    onSelectedDates() {
      this.page = 1
      this.startDate = this.selectedDates[0]
      this.endDate = this.selectedDates[1]

      this.updateQuery()
    },
    onClearDates() {
      this.page = 1
      this.selectedDates = null
      this.startDate = null
      this.endDate = null

      this.updateQuery()
    },
    onPageChange(page) {
      this.page = page

      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
    },
    async onExport() {
      if (!this.selectedLectureId) {
        this.$buefy.dialog.alert({
          title: '無法匯出',
          message: '請指定一個講座。',
          type: 'is-danger',
          hasIcon: true,
          icon: 'times-circle',
          iconPack: 'fa',
          ariaRole: 'alertdialog',
          ariaModal: true
        })
        return false
      }

      this.exporting = true
      this.isLoading = true
      const data = {
        area_code: this.selectedAreaCode,
        code: this.selectedLectureId,
        search: this.searchName,
        start_date: this.startDate,
        end_date: this.endDate
      }

      const api = `${LECTURE_ORDERS_API}/export`

      const config = {
        headers: { Accept: 'application/vnd.ms-excel' },
        responseType: 'blob'
      }

      try {
        const response = await this.axios.post(api, data, config)

        const date = this.$dayjs().format('YYYYMMDDHHmmss')
        const fileName = `${this.selectedLecture.topic}_報名單_${date}.xlsx`
        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('下載失敗', e)
      } finally {
        this.exporting = false
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss">
.lecture-table {
  .table {
    width: 1800px;
  }

  .product-name {
    max-width: 200px;

    p {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      @media screen and (max-width: 768px) {
        width: 200px;
      }
    }
  }
}
</style>
